import { useCallback, useEffect, useState } from 'react';
import { isStorefrontStorageEvent, loadStorefrontMode, saveStorefrontMode } from './storefrontMode.ts';

/**
 * MVP-500 Commit A. The React half of `storefrontMode.ts` — its own file so that module stays
 * React-free and testable under the `node` vitest project, the same split `i18n/locale.ts` and
 * `i18n/i18n.tsx` already use.
 *
 * The `storage` listener is what makes a second same-origin document (the Stage window) converge:
 * a document never receives its own `storage` event, so the writer below updates from local state
 * and there is no echo to guard against, while every other open document follows live.
 */
export function useStorefrontMode(): { active: boolean; setActive: (on: boolean) => void } {
  const [active, setActiveState] = useState(loadStorefrontMode);

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (isStorefrontStorageEvent(event.key)) setActiveState(loadStorefrontMode());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setActive = useCallback((on: boolean) => {
    setActiveState(on);
    saveStorefrontMode(on);
  }, []);

  return { active, setActive };
}
